import { useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';

const Section = ({ children, backgroundColor = 'transparent', textColor = 'inherit', className = '', ...props }) => {
	const sectionRef = useRef(null);
	const mainElementRef = useRef(null);
	const isForced = textColor !== 'inherit';

	const handleIntersection = useCallback(
		([entry]) => {
			if (!mainElementRef.current) return;

			if (entry.isIntersecting) {
				mainElementRef.current.style.setProperty('--section-bg-color', backgroundColor || 'transparent');
				mainElementRef.current.style.setProperty('--section-text-color', textColor || 'inherit');
				mainElementRef.current.classList.add('section-active');
				if (isForced) {
					mainElementRef.current.classList.add('section-forced');
				}
			} else {
				mainElementRef.current.style.setProperty('--section-bg-color', 'transparent');
				mainElementRef.current.style.setProperty('--section-text-color', 'inherit');
				mainElementRef.current.classList.remove('section-active');
				if (isForced) {
					mainElementRef.current.classList.remove('section-forced');
				}
			}
		},
		[backgroundColor, textColor, isForced]
	);

	useEffect(() => {
		mainElementRef.current = document.getElementById('main');
		if (!mainElementRef.current) return;

		const style = document.createElement('style');
		style.textContent = `
			:root {
				--section-bg-color: transparent;
				--section-text-color: inherit;
			}
			#main {
				background-color: var(--section-bg-color);
				transition: background-color 0.5s ease-in-out;
			}
			#main.section-active {
				background-color: var(--section-bg-color);
			}
			#main.section-active.section-forced *:not(button):not(a):not(img) {
				color: var(--section-text-color) !important;
				transition: color 0.5s ease-in-out;
			}
		`;
		document.head.appendChild(style);

		const observer = new IntersectionObserver(handleIntersection, {
			threshold: 0.2,
			rootMargin: '50px 0px',
		});

		const currentRef = sectionRef.current;
		if (currentRef) {
			observer.observe(currentRef);
		}

		return () => {
			observer.disconnect();
			if (mainElementRef.current) {
				mainElementRef.current.style.removeProperty('--section-bg-color');
				mainElementRef.current.style.removeProperty('--section-text-color');
				mainElementRef.current.classList.remove('section-active');
				mainElementRef.current.classList.remove('section-forced');
			}
			style.remove();
		};
	}, [handleIntersection]);

	return (
		<section ref={sectionRef} className={className} {...props}>
			{children}
		</section>
	);
};

Section.propTypes = {
	children: PropTypes.node.isRequired,
	backgroundColor: PropTypes.string,
	textColor: PropTypes.string,
	className: PropTypes.string,
};

export default Section;
